<script setup>
  import { ref, computed, useTemplateRef, watch, watchEffect, nextTick, onMounted, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n';
  import { useUserStore } from "@/stores/UserStore.js";
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';

  import { useCookie } from "@/composables/useCookie.js";
  import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
  import BaseModal from "@/components/base/BaseModal.vue";
  import Icon from "@/components/icons/Icon.vue";

  const props = defineProps({
    autoOpen: {
      type: Boolean,
      default: true
    }
  })

  const userStore = useUserStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();
  const cookie = useCookie();
  const { $BRAND } = useConfigurationGlobals();

  const { t } = useI18n();

  const messagesContainer = useTemplateRef('messages-container');

  const hasMessage = computed(() => !!userStore.messages?.[0]);
  const cookieName = `msg-${userStore.messages?.[0]?.Id}-dismissed`;
  const dismissalLength = userStore.messages?.[0]?.dismissal_length;
  const messageExpiration = dismissalLength
    ? new Date(Date.now() + dismissalLength)
    : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
  const messageType = parseInt(userStore.messages?.[0]?.Type);
  const promoShownCountCookieName = `promo-shown-count-${userStore.messages?.[0]?.Id}`;
  const promoShownCountExpiration = new Date(userStore.messages?.[0]?.expire_date);

  const hideMessage = ref(cookie.get(cookieName) === 'true');
  const showModal = ref(hasMessage.value && !hideMessage.value && props.autoOpen);
  const promoShownCount = ref(parseInt(cookie.get(promoShownCountCookieName)) || 0);

  const message = computed(() => `${userStore.messages?.[0]?.Message ?? ''}`);
  const iconName = computed(() => messageType === 401 ? 'premium-badge' : messageType === 402 ? 'stopwatch' : false);
  const messageClasses = computed(() => ['jwx-promo-modal', {'jwx-promo-modal--icon': iconName.value}]);

  cookie.set(cookieName, hideMessage.value, { expires: messageExpiration });

  const persistPromoShownCount = () => {
    cookie.set(promoShownCountCookieName, promoShownCount.value, { expires: promoShownCountExpiration });
  }

  const attachedAnalyticsToMessageLinks = (links) => {
    if (!links) return;

    links.forEach(link => {
      const isUpgradeLink = link.href.includes('signup');
      const url = new URL(link.href);
      const eventName = isUpgradeLink ? 'Promo_Upgrade_Tapped' : 'Promo_Link_Clicked'

      link.addEventListener('click', (event) => {
        event.preventDefault();

        googleAnalyticsStore.sendEvent(eventName, {
          "Hostname": url.hostname,
          "Pathname": url.pathname,
        });

        // Reset the open count once a user converts via an upgrade link.
        if (isUpgradeLink) {
          promoShownCount.value = 0;
          persistPromoShownCount();
        }

          window.location = link.href;
      });
    });
  }

  const wireUpMessageContent = async () => {
    await nextTick();
    if (!messagesContainer.value) return;

    const messageLinks = messagesContainer.value.querySelectorAll('.jwx-promo-modal__text a');

    attachedAnalyticsToMessageLinks(messageLinks);
  }

  const handleShowPromoModal = () => {
    if (!hasMessage.value) return;
    showModal.value = true;
    hideMessage.value = false;

    googleAnalyticsStore.sendEvent('Promo_Shown', {
      "Promo_Shown_Count": promoShownCount.value,
    });    
  }

  const handleDismiss = () => {
    hideMessage.value = true;
    showModal.value = false;

    googleAnalyticsStore.sendEvent("Promo_Skip", {
      "Promo_Shown_Count": promoShownCount.value,
    });
  }

  onMounted(() => {
    window.$EventBus.addEventListener('jwx-show-promo-modal', handleShowPromoModal);

    // The watcher below only fires on subsequent opens, so count the auto-open here.
    if (showModal.value) {
      promoShownCount.value++;
      persistPromoShownCount();
    }
  });

  onUnmounted(() => {
    window.$EventBus.removeEventListener('jwx-show-promo-modal', handleShowPromoModal);
  });

  watchEffect(() => cookie.set(cookieName, hideMessage.value, { expires: messageExpiration }));

  watch(showModal, (visible) => {
    if (visible) {
      promoShownCount.value++;
      persistPromoShownCount();
      wireUpMessageContent();
    }
  });

</script>

<template>
  <BaseModal
    v-if="hasMessage"
    v-model="showModal"
    :unstyled="true"
    styleClasses="jwx-promo-modal-container"
    hide-close-button
  >
    <div class="jwx-map__messages">
      <div
        :class="messageClasses"
        :data-message-type="userStore.messages[0].Type"
        :data-brand="$BRAND.toLowerCase()"
      >
        <div class="jwx-promo-modal__header">
          <div class="jwx-promo-modal__header-background">
            <img src="../assets/images/promo/wave-image@2x.png" alt="" class="jwx-promo-modal__header-background-image" />
          </div>
          <img src="../assets/images/promo/ikite-website-promo-devices@2x.png" alt="" class="jwx-promo-modal__hero" />
        </div>
        <div class="jwx-promo-modal__body">
          <Icon v-if="iconName" :name="iconName" class="jwx-promo-modal__icon" />
          <p class="jwx-promo-modal__content">
            <span ref="messages-container" v-html="message" class="jwx-promo-modal__text"></span>
          </p>
          <div class="jwx-promo-modal__dismiss" @click.prevent="handleDismiss">{{ t('common.notNow') }}</div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>